import { useState, type MouseEvent } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import Box from '@liquidweb-dev/undercurrent/Box';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import ConstructionIcon from '@liquidweb-dev/undercurrent/icons/lucide/Construction';
import HistoryIcon from '@liquidweb-dev/undercurrent/icons/lucide/History';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import useAccountDetails from 'modules/queries/account/useDetails';
import sendGtmEvent from 'utility/gtm/sendGtmEvent';
import BackupSettings from 'pages/servers/Backups/BackupsBlock/Settings';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { BackupsDrawerProvider } from './BackupsDrawerContext';

interface BackupsChipProps {
	uniqId: string;
	displayType?: string;
	canBackups: boolean;
	hasBackups: boolean;
	acronisStatus?: string;
	/** An Acronis backup was ordered for the server, but the server does not list it yet. */
	isNewOrdered?: boolean;
}

const BackupsChip = ({
	uniqId,
	displayType,
	canBackups,
	hasBackups,
	acronisStatus,
	isNewOrdered,
}: BackupsChipProps) => {
	const history = useHistory();
	const { data: accountData } = useAccountDetails({});
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const backupsUrl = `/servers/${uniqId}/backups`;
	const isSettingUp =
		isNewOrdered || (hasBackups && acronisStatus && acronisStatus !== 'Active');

	const handleBackupSuccess = () => {
		setIsDrawerOpen(false);
		sendGtmEvent({
			event: 'lw.event',
			eventName: 'no_backups_alert_add',
			eventData: {
				account_id: accountData?.accnt,
				server_id: uniqId,
				product_type: displayType,
				timestamp: new Date().toISOString(),
				system: 'lw',
			},
		});
		history.push(backupsUrl);
	};

	if (!canBackups) return null;

	const handleNavigateClick = (event: MouseEvent) => {
		event.stopPropagation();
	};

	const handleOpenDrawer = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	if (isSettingUp) {
		return (
			<Tooltip title="Setting up Backups...">
				<IconButton
					variant="secondary"
					color="highlight"
					size="small"
					onClick={handleNavigateClick}
					component={RouterLink}
					to={backupsUrl}
				>
					<ConstructionIcon />
				</IconButton>
			</Tooltip>
		);
	}

	if (hasBackups) {
		return (
			<Tooltip title="Backups & Recovery">
				<IconButton
					variant="secondary"
					color="success"
					size="small"
					onClick={handleNavigateClick}
					component={RouterLink}
					to={backupsUrl}
					aria-label="Backups enabled"
				>
					<HistoryIcon />
				</IconButton>
			</Tooltip>
		);
	}

	return (
		<Box>
			<Tooltip
				variant="fancy"
				arrow
				title="Backups not configured"
				message="Without backups, data loss from outages or mistakes may be permanent."
				sx={{ cursor: 'pointer' }}
			>
				<IconButton
					variant="upsell"
					color="warning"
					size="small"
					onClick={handleOpenDrawer}
				>
					<>
						<TriangleAlertIcon />
						<Typography
							variant="label-sm"
							sx={{
								fontWeight: '500',
								lineHeight: 0,
								whiteSpace: 'nowrap',
							}}
						>
							No Backups
						</Typography>
					</>
				</IconButton>
			</Tooltip>
			<Box
				onClick={(event) => {
					// Prevents clicks from bubbling up to the parent RouterLink (which would cause navigation)
					event.stopPropagation();
				}}
			>
				<BackupsDrawerProvider value={{ onSuccess: handleBackupSuccess }}>
					<BackupSettings
						uniqId={uniqId}
						open={isDrawerOpen}
						onClose={handleCloseDrawer}
					/>
				</BackupsDrawerProvider>
			</Box>
		</Box>
	);
};

export default BackupsChip;
